import { type ReactNode } from 'react';
import { SYMBOL_COUNT } from '@/pages/dsst-spick/dsstConfig';

interface SymbolProps {
  symbolId: number;
  setIndex?: number;
  className?: string;
}

// 제공된 심볼 세트 공통 스타일: 64px 그리드 / 6px 스트로크 / 라운드 조인
const SVG_PROPS = {
  viewBox: '0 0 64 64',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const ROTATE_STEPS = [0, 30, 60, 90, 120, 150];

function Svg({ className, children }: { className: string; children: ReactNode }) {
  return (
    <svg {...SVG_PROPS} className={`inline-block shrink-0 ${className}`}>
      {children}
    </svg>
  );
}

// 세트 A — 서로 다른 도형 6종
function renderSet0(id: number, className: string) {
  switch (id) {
    case 0:
      return (
        <Svg className={className}>
          <circle cx="32" cy="32" r="21" />
        </Svg>
      );
    case 1:
      return (
        <Svg className={className}>
          <path d="M32 10V54M10 32H54" />
        </Svg>
      );
    case 2:
      return (
        <Svg className={className}>
          <path d="M32 10L55 52H9Z" />
        </Svg>
      );
    case 3:
      return (
        <Svg className={className}>
          <path d="M11 11H53V53H11Z" />
        </Svg>
      );
    case 4:
      return (
        <Svg className={className}>
          <path d="M10 21H54M10 43H54" />
        </Svg>
      );
    case 5:
      return (
        <Svg className={className}>
          <path d="M10 12H52L12 52H54" />
        </Svg>
      );
    default:
      return null;
  }
}

// 세트 B — 서로 다른 도형 6종
function renderSet1(id: number, className: string) {
  switch (id) {
    case 0:
      return (
        <Svg className={className}>
          <path d="M32 9L55 32L32 55L9 32Z" />
        </Svg>
      );
    case 1:
      return (
        <Svg className={className}>
          <path d="M15 15L49 49M49 15L15 49" />
        </Svg>
      );
    case 2:
      return (
        <Svg className={className}>
          <path d="M21 11H43L55 32L43 53H21L9 32Z" />
        </Svg>
      );
    case 3:
      return (
        <Svg className={className}>
          <path d="M12 12V34C12 47 20 54 32 54C44 54 52 47 52 34V12" />
        </Svg>
      );
    case 4:
      return (
        <Svg className={className}>
          <path d="M21 10V54M43 10V54" />
        </Svg>
      );
    case 5:
      return (
        <Svg className={className}>
          <path d="M10 49L32 12L54 49" />
        </Svg>
      );
    default:
      return null;
  }
}

// 세트 C — 사각형 + 회전선(방향 맞추기)
function renderSet2(id: number, className: string) {
  const deg = ROTATE_STEPS[id];
  if (deg === undefined) return null;
  return (
    <Svg className={className}>
      <rect x="11" y="11" width="42" height="42" rx="3" />
      <g transform={`rotate(${deg} 32 32)`}>
        <path d="M18 32H46" />
      </g>
    </Svg>
  );
}

// 세트 D — 원 + 회전선(방향 맞추기)
function renderSet3(id: number, className: string) {
  const deg = ROTATE_STEPS[id];
  if (deg === undefined) return null;
  return (
    <Svg className={className}>
      <circle cx="32" cy="32" r="21" />
      <g transform={`rotate(${deg} 32 32)`}>
        <path d="M18 32H46" />
      </g>
    </Svg>
  );
}

export default function Symbol({ symbolId, setIndex = 0, className = '' }: SymbolProps) {
  const safeId = ((symbolId % SYMBOL_COUNT) + SYMBOL_COUNT) % SYMBOL_COUNT;
  const safeSet = ((setIndex % 4) + 4) % 4;

  if (safeSet === 0) return renderSet0(safeId, className);
  if (safeSet === 1) return renderSet1(safeId, className);
  if (safeSet === 2) return renderSet2(safeId, className);
  return renderSet3(safeId, className);
}